import SendIcon from "@mui/icons-material/Send";
import TextField from "@mui/material/TextField";
import { styled } from "@mui/material/styles";
import { useAttorneyHubDispatch } from "_shared/_stores/redux-store";
import { KeyboardKey } from "_shared/_utilities/keyboard-keys";
import Loader from "_shared/_views/loading/loader";
import LoadingButton from "_shared/_views/loading/loading-button";
import EntityMember from "_shared/entity-members/entity-member";
import { CanceledError } from "axios";
import {
  getIndividualByUserId,
} from "marketplace/individuals/_stores/individuals-redux-slice";
import { useGetIndividualsByUserIdsQuery } from "marketplace/individuals/_api/individual-api";
import { marketplaceTeamsApi } from "marketplace/teams/_api/marketplace-team-api";
import Team from "marketplace/teams/team";
import { enqueueSnackbar } from "notistack";
import React, { useCallback, useEffect, useMemo } from "react";
import { useSelector } from "react-redux";
import { useSession } from "users/__session/session-context";
import ProposalBuilder from "work/proposals/__builder/proposal-builder";
import { useCreateProposalMutation, useSubmitProposalMutation } from "work/proposals/_api/proposal-api";
import ProposalIssues from "work/proposals/issues/proposal-issues";
import Proposal from "work/proposals/proposal";
import ProjectDescription from "work/work-agreements/project-description/project-description";
import ProjectName from "work/work-agreements/project-name/project-name";
import SelectedVendorTeam from "work/work-agreements/teams/vendor-team/selection/selected-vendor-team/selected-vendor-team";
import VendorTeam from "work/work-agreements/teams/vendor-team/vendor-team";

const Content = styled("section")(() => ({
  display: "flex",
  flex: 1,
  flexDirection: "row",
}));
const ProposalActions = styled("section")(({ theme }) => ({
  display: "flex",
  flexDirection: "row",
  flexGrow: 1,
  flexWrap: "nowrap",
  columnGap: theme.spacing(1),
  marginTop: theme.spacing(2),
}));
const ProposalActionButton = styled(LoadingButton)(() => ({
  width: "100%",
  whiteSpace: "nowrap",
  "&.Mui-disabled": {
    color: "rgba(0, 0, 0, 0.26) !important",
    backgroundColor: "rgba(0, 0, 0, 0.12) !important",
  },
}));

type ProposalRequestDraftDialogProps = {
  onProposalSubmitted?: (proposal: Proposal) => void;
  onCancel: () => void;
  searchParams?: URLSearchParams;
};

export default function ProposalRequestDraftDialog(props: Readonly<ProposalRequestDraftDialogProps>) {
  const { onProposalSubmitted, onCancel, searchParams } = props;

  // #region Hooks
  const session = useSession();
  const dispatch = useAttorneyHubDispatch();
  const effectiveSearchParams = useMemo(
    () => searchParams ?? new URLSearchParams(globalThis.location.search),
    [searchParams],
  );
  const preSelectedLeader = useMemo(
    () => EntityMember.fromURLSearchParams(effectiveSearchParams),
    [effectiveSearchParams],
  );
  const preSelectedTeam = useMemo(
    () => SelectedVendorTeam.fromURLSearchParams(effectiveSearchParams),
    [effectiveSearchParams],
  );

  // #region Queries
  const preselectedLeaderIndividual = useSelector(getIndividualByUserId(preSelectedLeader?.userId));
  useGetIndividualsByUserIdsQuery(
    { userIds: new Set(preSelectedLeader?.userId ? [preSelectedLeader.userId.value] : []) },
    { skip: !preSelectedLeader?.userId || !!preselectedLeaderIndividual },
  );
  const [createProposalQuery] = useCreateProposalMutation();
  const [submitProposalQuery] = useSubmitProposalMutation();
  const [isLoading, setIsLoading] = React.useState<boolean>(false);
  const [isSubmitting, setIsSubmitting] = React.useState<boolean>(false);
  const [descriptionValue, setDescriptionValue] = React.useState<string>("");
  const [proposalBuilder, setProposalBuilder] = React.useState<ProposalBuilder>(() => new ProposalBuilder({ session }));

  // #endregion

  const buildProposalWithPreselectedLeader = useCallback((builder: ProposalBuilder, leader: EntityMember) => {
    // TODO: Validate that leader has access
    return builder
      .setClient(session.user?.toEntityMember())
      .setName(new ProjectName("New Proposal"))
      .setVendorTeam(new VendorTeam({ leader: leader, members: [] }));
  }, [session]);

  const buildProposalWithPreselectedTeam = useCallback(async (
    builder: ProposalBuilder,
    preSelectedTeam: SelectedVendorTeam,
  ) => {
    try {
      const teamJSON = await dispatch(marketplaceTeamsApi.endpoints.getTeamById.initiate({ teamId: preSelectedTeam.targetTeamId.value })).unwrap();
      if (!teamJSON) throw new Error("Failed to load team");
      const team = Team.fromJSON(teamJSON);
      if (!team) throw new Error("Failed to load team");
      if (!team.leader?.userId || !team.leader?.legalEntityId) return builder;

      return builder
        .setClient(session.user?.toEntityMember())
        .setName(new ProjectName("New Proposal"))
        .setVendorTeam(team.toWorkTeam());
    } catch (error) {
      if (error instanceof CanceledError) return builder;
      console.error(error);
      return builder;
    }
  }, [dispatch, session]);

  const initProposal = useCallback(async (isActive: () => boolean = () => true) => {
    try {
      setIsLoading(true);
      let nextProposalBuilder = new ProposalBuilder({ session });

      if (preSelectedLeader) {
        nextProposalBuilder = buildProposalWithPreselectedLeader(nextProposalBuilder, preSelectedLeader);
      }
      if (preSelectedTeam) {
        nextProposalBuilder = await buildProposalWithPreselectedTeam(nextProposalBuilder, preSelectedTeam);
      }
      if (isActive()) {
        setProposalBuilder(nextProposalBuilder);
      }
    } catch (error) {
      if (error instanceof CanceledError) return;
      console.error(error);
    } finally {
      if (isActive()) {
        setIsLoading(false);
      }
    }
  }, [
    buildProposalWithPreselectedLeader,
    buildProposalWithPreselectedTeam,
    preSelectedLeader,
    preSelectedTeam,
    session
  ]);

  useEffect(() => {
    let active = true;
    queueMicrotask(() => {
      if (!active) return;
      void initProposal(() => active);
    });
    return () => {
      active = false;
    };
  }, [initProposal]);

  async function handleSubmitClicked() {
    try {
      setIsSubmitting(true);
      const issues = ProposalIssues.fromBuilder(proposalBuilder);
      if (!issues.canSubmit) return;
      const draftProposal = proposalBuilder.buildNewDraft();
      const createdProposalJSON = await createProposalQuery({ proposal: draftProposal, session }).unwrap();
      if (!createdProposalJSON.id) throw new Error("Created proposal is missing an ID");

      const submittedProposalJSON = await submitProposalQuery({ proposalId: createdProposalJSON.id }).unwrap();
      const submittedProposal = Proposal.fromJSON({
        json: submittedProposalJSON,
        currentUser: session.user!,
      });
      if (!submittedProposal) throw new Error("Failed to deserialize submitted proposal");

      onProposalSubmitted?.(submittedProposal);
    } catch (error) {
      console.error(error);
      enqueueSnackbar("Failed to submit proposal", { variant: "error" });
    } finally {
      setIsSubmitting(false);
    }
  }

  function handleDescriptionChanged(event: React.ChangeEvent<HTMLInputElement>) {
    setDescriptionValue(event.target.value);

    setProposalBuilder((builder) => builder.setDescription(new ProjectDescription(event.target.value)));
  }

  const issues = useMemo(() => ProposalIssues.fromBuilder(proposalBuilder), [proposalBuilder]);

  return (
    <>
      <Content>
        {isLoading && <Loader />}
        {!isLoading && (
          <TextField
            fullWidth
            multiline
            minRows={3}
            placeholder="Enter a brief description of what you need"
            value={descriptionValue}
            onChange={handleDescriptionChanged}
            onKeyDown={(event) => {
              if (event.key === KeyboardKey.Enter) {
                void handleSubmitClicked();
              }
            }}
          />
        )}
      </Content>
      <ProposalActions>
        <ProposalActionButton
          variant="contained"
          color="primary"
          startIcon={<SendIcon />}
          loading={isSubmitting}
          disabled={isLoading || !issues.canSubmit}
          onClick={handleSubmitClicked}
        >
          Submit
        </ProposalActionButton>
        <ProposalActionButton
          variant="contained"
          loading={false}
          disabled={isLoading}
          onClick={onCancel}
        >
          Cancel
        </ProposalActionButton>
      </ProposalActions>
    </>
  );
}
